import type { TasksKanbanLogsFilterParams } from '@ts-tasks-kanban/interfaces'
import type { KanbanLog } from '@ts-kanban-logs/interfaces'
import type { Pagination } from '@ts-common/interfaces'

import { useTasksKanbanStore } from '@stores/tasks-kanban'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

interface TasksKanbanLogsApiOptions {
  id: string | number
  params?: TasksKanbanLogsFilterParams
  cursor?: string
}

interface TasksKanbanLogsApiResponse {
  logs: KanbanLog[]
  meta: Pagination
}

const buildQueryParams = (params?: TasksKanbanLogsFilterParams): Record<string, string> => {
  if (!params) return {}

  const query: Record<string, string> = {}

  if (params.query) query.query = params.query
  if (params.date_from) query.date_from = params.date_from
  if (params.date_to) query.date_to = params.date_to

  return query
}

export const useTasksKanbanLogsApi = async (options: TasksKanbanLogsApiOptions): Promise<KanbanLog[]> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const isLoadMore = !!options.cursor

  const url = useAdminRoutes('admin.kanban-tasks.logs', {
    id: options.id,
    ...buildQueryParams(options.params),
    ...(options.cursor ? { cursor: options.cursor } : {}),
  })

  const tasksKanbanStore = useTasksKanbanStore()
  const commonStore = useCommonStore()

  if (!isLoadMore) commonStore.setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const response: TasksKanbanLogsApiResponse = data.data

    if (isLoadMore) {
      tasksKanbanStore.appendLogs(response.logs ?? [])
    } else {
      tasksKanbanStore.setLogs(response.logs ?? [])
    }
    tasksKanbanStore.setLogsPagination(response.meta)

    if (!isLoadMore) commonStore.setLoading(false)

    return response.logs ?? []
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    if (!isLoadMore) commonStore.setLoading(false)

    return []
  }
}
